import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { addEnchargeTags, upsertEnchargePerson } from "./encharge/api";
import type {
  EnchargeInstance,
  EnchargePerson,
  EnchargePersonInput,
} from "./encharge/types";
import { EnchargeTagsEditor } from "./EnchargeTagsEditor";

type PersonFormState = {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  postcode: string;
  contactPerson: string;
  tags: string[];
  communicationCategoryIds: Record<string, boolean>;
};

function emptyForm(categoryIds: string[]): PersonFormState {
  return {
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    phone: "",
    postcode: "",
    contactPerson: "",
    tags: [],
    communicationCategoryIds: Object.fromEntries(
      categoryIds.map((id) => [id, false]),
    ),
  };
}

function personToForm(person: EnchargePerson): PersonFormState {
  return {
    id: person.id,
    email: person.email,
    firstName: person.firstName,
    lastName: person.lastName,
    company: person.company,
    phone: person.phone,
    postcode: person.postcode,
    contactPerson: person.contactPerson,
    tags: person.tags ?? [],
    communicationCategoryIds: { ...person.communicationCategoryIds },
  };
}

export const EmailListPersonDialog = ({
  mode,
  instance,
  open,
  onOpenChange,
  person,
  categories,
  existingEmails = [],
  onSaved,
  onTagsChange,
}: {
  mode: "create" | "edit";
  instance: EnchargeInstance;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person?: EnchargePerson | null;
  categories: Record<string, string>;
  existingEmails?: string[];
  onSaved: () => void;
  onTagsChange?: (tags: string[]) => void;
}) => {
  const translate = useTranslate();
  const categoryIds = Object.keys(categories);
  const [form, setForm] = useState<PersonFormState>(() =>
    person ? personToForm(person) : emptyForm(categoryIds),
  );
  const [error, setError] = useState<string | null>(null);
  const [existsPrompt, setExistsPrompt] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(person ? personToForm(person) : emptyForm(categoryIds));
    setError(null);
    setExistsPrompt(false);
  }, [open, person, categoryIds.join(",")]);

  const resetForm = () => {
    setForm(person ? personToForm(person) : emptyForm(categoryIds));
    setError(null);
    setExistsPrompt(false);
  };

  const mutation = useMutation({
    mutationFn: async (input: EnchargePersonInput & { tags: string[] }) => {
      await upsertEnchargePerson(instance, input);
      if (mode === "create" && input.tags.length > 0) {
        await addEnchargeTags(instance, {
          tags: input.tags,
          email: input.email,
          personId: input.id,
        });
      }
    },
    onSuccess: () => {
      onSaved();
      onOpenChange(false);
      resetForm();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const validate = (): string | null => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      return translate("crm.email_lists.validation.required_fields", {
        _: "First name, last name, and email are required.",
      });
    }
    const hasCategory = Object.values(form.communicationCategoryIds).some(
      Boolean,
    );
    if (!hasCategory) {
      return translate("crm.email_lists.validation.category_required", {
        _: "Select at least one communication category.",
      });
    }
    return null;
  };

  const submit = (forceUpdate = false) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const emailLower = form.email.trim().toLowerCase();
    const exists =
      mode === "create" &&
      existingEmails.some((e) => e.toLowerCase() === emailLower);

    if (exists && !forceUpdate) {
      setExistsPrompt(true);
      return;
    }

    setExistsPrompt(false);
    setError(null);

    mutation.mutate({
      id: form.id,
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      company: form.company.trim(),
      phone: form.phone.trim(),
      postcode: form.postcode.trim(),
      contactPerson: form.contactPerson.trim(),
      communicationCategoryIds: form.communicationCategoryIds,
      tags: form.tags,
    });
  };

  const title =
    mode === "create"
      ? translate("crm.email_lists.create_title", { _: "Create contact" })
      : translate("crm.email_lists.edit_title", { _: "Edit contact" });

  const handleTagsChange = (tags: string[]) => {
    setForm((f) => ({ ...f, tags }));
    onTagsChange?.(tags);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="encharge-email">
              {translate("crm.email_lists.fields.email", { _: "Email" })}
              {mode === "create" ? " *" : ""}
            </Label>
            <Input
              id="encharge-email"
              type="email"
              value={form.email}
              disabled={mode === "edit"}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="encharge-first">
                {translate("crm.email_lists.fields.first_name", {
                  _: "First name",
                })}{" "}
                *
              </Label>
              <Input
                id="encharge-first"
                value={form.firstName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, firstName: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="encharge-last">
                {translate("crm.email_lists.fields.last_name", {
                  _: "Last name",
                })}{" "}
                *
              </Label>
              <Input
                id="encharge-last"
                value={form.lastName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, lastName: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="encharge-company">
                {translate("crm.email_lists.fields.company", { _: "Company" })}
              </Label>
              <Input
                id="encharge-company"
                value={form.company}
                onChange={(e) =>
                  setForm((f) => ({ ...f, company: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="encharge-phone">
                {translate("crm.email_lists.fields.phone", { _: "Phone" })}
              </Label>
              <Input
                id="encharge-phone"
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: e.target.value }))
                }
              />
            </div>
          </div>

          <div>
            <Label htmlFor="encharge-postcode">
              {translate("crm.email_lists.fields.postcode", { _: "Postcode" })}
            </Label>
            <Input
              id="encharge-postcode"
              value={form.postcode}
              onChange={(e) =>
                setForm((f) => ({ ...f, postcode: e.target.value }))
              }
            />
          </div>

          <div>
            <Label htmlFor="encharge-contact-person">
              {translate("crm.email_lists.fields.contact_person", {
                _: "Gobo contact person",
              })}
            </Label>
            <Input
              id="encharge-contact-person"
              value={form.contactPerson}
              onChange={(e) =>
                setForm((f) => ({ ...f, contactPerson: e.target.value }))
              }
            />
          </div>

          <div>
            <Label className="mb-2 block">
              {translate("crm.email_lists.fields.tags", { _: "Tags" })}
            </Label>
            <EnchargeTagsEditor
              instance={instance}
              tags={form.tags}
              onChange={handleTagsChange}
              personEmail={mode === "edit" ? form.email : undefined}
              personId={mode === "edit" ? form.id : undefined}
              persistImmediately={mode === "edit"}
              disabled={mutation.isPending}
            />
          </div>

          <div>
            <Label className="mb-2 block">
              {translate("crm.email_lists.fields.categories", {
                _: "Communication categories",
              })}{" "}
              *
            </Label>
            <div className="space-y-2">
              {Object.entries(categories).map(([catId, label]) => (
                <label
                  key={catId}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={!!form.communicationCategoryIds[catId]}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({
                        ...f,
                        communicationCategoryIds: {
                          ...f.communicationCategoryIds,
                          [catId]: checked === true,
                        },
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {error ? <p className="text-destructive text-sm">{error}</p> : null}

          {existsPrompt ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
              <p>
                {translate("crm.email_lists.exists_prompt", {
                  _: "A contact with this email already exists. Update the existing contact instead?",
                })}
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => submit(true)}
                  disabled={mutation.isPending}
                >
                  {translate("crm.email_lists.update_existing", {
                    _: "Update existing",
                  })}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setExistsPrompt(false)}
                >
                  {translate("ra.action.cancel", { _: "Cancel" })}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {translate("ra.action.cancel", { _: "Cancel" })}
          </Button>
          <Button
            type="button"
            onClick={() => submit(false)}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Spinner className="size-4 mr-2" />
                {translate("crm.email_lists.saving", { _: "Saving..." })}
              </>
            ) : (
              translate("ra.action.save", { _: "Save" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
